import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { TIER_CONFIG, TIER_ORDER, type TierType } from '@/types/engagement';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useLanguage } from '@/i18n/LanguageContext';

interface TierProgressBarProps {
  currentTier: TierType;
  totalPoints: number;
  className?: string;
}

// Level key to i18n key mapping
const LEVEL_KEY_TO_I18N: Record<string, 'heartOpener' | 'soulConnector' | 'loveAmbassador' | 'gratitudeChampion' | 'legacyMaker'> = {
  'Heart Opener': 'heartOpener',
  'Soul Connector': 'soulConnector',
  'Love Ambassador': 'loveAmbassador',
  'Gratitude Champion': 'gratitudeChampion',
  'Legacy Maker': 'legacyMaker',
};

// Level display with emojis matching the landing page
const LEVEL_DISPLAY = [
  { key: 'Heart Opener', points: 0, emoji: '💚' },
  { key: 'Soul Connector', points: 50, emoji: '💙' },
  { key: 'Love Ambassador', points: 100, emoji: '💜' },
  { key: 'Gratitude Champion', points: 175, emoji: '💛' },
  { key: 'Legacy Maker', points: 250, emoji: '❤️' },
];

// Get current level based on points
function getCurrentLevel(totalPoints: number) {
  let currentLevel = LEVEL_DISPLAY[0];
  for (const level of LEVEL_DISPLAY) {
    if (totalPoints >= level.points) {
      currentLevel = level;
    }
  }
  return currentLevel;
}

// Get next level based on points
function getNextLevel(totalPoints: number) {
  for (const level of LEVEL_DISPLAY) {
    if (totalPoints < level.points) {
      return level;
    }
  }
  return null;
}

export function TierProgressBar({ currentTier, totalPoints, className }: TierProgressBarProps) {
  const { t } = useLanguage();
  const currentLevel = getCurrentLevel(totalPoints);
  const nextLevel = getNextLevel(totalPoints);

  // Get translated level name
  const getLevelName = (levelKey: string) => {
    const i18nKey = LEVEL_KEY_TO_I18N[levelKey];
    return i18nKey ? t.howToUse.gamification.levels[i18nKey] : levelKey;
  };

  // Calculate progress to next level
  const getProgressPercent = () => {
    if (!nextLevel) return 100;
    const prevLevelPoints = currentLevel.points;
    const nextLevelPoints = nextLevel.points;
    const progress = ((totalPoints - prevLevelPoints) / (nextLevelPoints - prevLevelPoints)) * 100;
    return Math.min(100, Math.max(0, progress));
  };

  // Dynamic progress context line with i18n
  const getProgressContextLine = () => {
    if (!nextLevel) {
      return t.howToUse.gamification.progressContext.maxLevel;
    }
    const pointsNeeded = nextLevel.points - totalPoints;
    return t.howToUse.gamification.progressContext.nextMilestone
      .replace('{points}', String(pointsNeeded))
      .replace('{level}', getLevelName(nextLevel.key));
  };

  return (
    <TooltipProvider>
      <div className={cn('space-y-4', className)}>
        {/* Level Pills - matching landing page style */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-2">{t.howToUse.gamification.levelsTitle}</h4>
          <p className="text-xs text-muted-foreground mb-3">{getProgressContextLine()}</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {LEVEL_DISPLAY.map((level, index) => {
              const isAchieved = totalPoints >= level.points;
              const isCurrent = currentLevel.key === level.key;
              const levelName = getLevelName(level.key);
              
              return (
                <Tooltip key={level.key}>
                  <TooltipTrigger asChild>
                    <motion.div
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all cursor-help',
                        isAchieved
                          ? 'bg-muted border border-primary/30'
                          : 'bg-muted/50 border border-transparent',
                        isCurrent && 'ring-2 ring-primary ring-offset-2'
                      )}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.08, type: 'spring' }}
                    >
                      <span className="text-base">{level.emoji}</span>
                      <span className={cn(
                        'font-medium',
                        isAchieved ? 'text-foreground' : 'text-muted-foreground'
                      )}>
                        {levelName}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({level.points} pts)
                      </span>
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="font-medium">{levelName}</p>
                    <p className="text-xs text-muted-foreground">
                      {isAchieved 
                        ? `✓ ${t.howToUse.gamification.achieved}` 
                        : t.howToUse.gamification.ptsToGo.replace('{points}', String(level.points - totalPoints))}
                    </p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>

        {/* Progress bar with gradient */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="font-medium text-foreground flex items-center gap-2">
              {currentLevel.emoji} {getLevelName(currentLevel.key)}
            </span>
            <span className="text-muted-foreground">
              {nextLevel 
                ? t.howToUse.gamification.ptsToGo.replace('{points}', String(nextLevel.points - totalPoints))
                : t.howToUse.gamification.progressContext.maxLevel}
            </span>
          </div>
          
          <div className="relative h-3 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-accent origin-left"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: getProgressPercent() / 100 }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
          
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t.howToUse.gamification.pointsTotal.replace('{points}', String(totalPoints))}</span>
            {nextLevel && (
              <span>{t.howToUse.gamification.next} {nextLevel.emoji} {getLevelName(nextLevel.key)}</span>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
